import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, AlertTriangle, Pencil, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { Dialog, Field, FormRow, EmptyState, ErrorState, TableSkeleton, OverflowMenu, fmtDate, plural } from '../../components/ui';
import StaffHeader from './StaffHeader';
import { useStaffRecord, useStableCallback, IconButton, toDay } from './hrKit';

const STATUS = {
  issued:       { label: 'Issued',       cls: 'badge-red' },
  acknowledged: { label: 'Acknowledged', cls: 'badge-yellow' },
  resolved:     { label: 'Resolved',     cls: 'badge-green' },
  pending:      { label: 'Pending',      cls: 'badge-gray' },
};
const dateOf = (v) => (v ? String(v).slice(0, 10) : '');
const showDate = (v) => (v ? fmtDate(dateOf(v)) : '—');
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

function WarningDialog({ open, staffId, warning, onSaved, onClose }) {
  const isEdit = !!warning;
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(warning ? {
      issue_of_concern: warning.issue_of_concern || '', details_of_discussion: warning.details_of_discussion || '',
      employer_required_actions: warning.employer_required_actions || '', date_communicated: dateOf(warning.date_communicated),
      next_review_date: dateOf(warning.next_review_date), status: warning.status || 'issued',
    } : { issue_of_concern: '', details_of_discussion: '', employer_required_actions: '', date_communicated: todayKey(), next_review_date: '', status: 'issued' });
  }, [open, warning]);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); };
  const close = useStableCallback(() => { if (!saving) onClose(); });

  async function save(e) {
    e.preventDefault();
    if (!String(form.issue_of_concern || '').trim()) { setErrors({ issue_of_concern: 'Describe the issue of concern.' }); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/staff/${staffId}/warnings/${warning.id}`, form);
      else await api.post(`/staff/${staffId}/warnings`, form);
      toast.success(isEdit ? 'Warning updated' : 'Warning issued');
      setSaving(false); onSaved(); onClose();
    } catch (err) { setErrors({ form: err.response?.data?.message || "The warning wasn't saved. Try again." }); setSaving(false); }
  }

  return (
    <Dialog open={open} onClose={close} size="lg" title={isEdit ? 'Edit warning' : 'Issue warning'}
      footer={(
        <>
          {errors.form && <span className="mr-auto text-[13px] text-rose-700" role="alert">{errors.form}</span>}
          <button type="button" onClick={close} className="btn-secondary" disabled={saving}>Cancel</button>
          <button type="submit" form="warning-form" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Issue warning')}</button>
        </>
      )}>
      <form id="warning-form" onSubmit={save} className="space-y-4">
        <Field label="Issue of concern" required error={errors.issue_of_concern}>
          {(id) => <input id={id} className="input-field" value={form.issue_of_concern || ''} onChange={(e) => set('issue_of_concern', e.target.value)} placeholder="A short description of the issue" />}
        </Field>
        <Field label="Details of the discussion">
          {(id) => <textarea id={id} className="input-field" rows={4} value={form.details_of_discussion || ''} onChange={(e) => set('details_of_discussion', e.target.value)} placeholder="What was discussed with the staff member" />}
        </Field>
        <Field label="Actions they need to take">
          {(id) => <textarea id={id} className="input-field" rows={3} value={form.employer_required_actions || ''} onChange={(e) => set('employer_required_actions', e.target.value)} />}
        </Field>
        <FormRow cols={3}>
          <Field label="Status">
            {(id) => (
              <select id={id} className="input-field" value={form.status || 'issued'} onChange={(e) => set('status', e.target.value)}>
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            )}
          </Field>
          <Field label="Date communicated">
            {(id) => <input id={id} type="date" className="input-field" value={form.date_communicated || ''} onChange={(e) => set('date_communicated', e.target.value)} />}
          </Field>
          <Field label="Next review">
            {(id) => <input id={id} type="date" className="input-field" value={form.next_review_date || ''} onChange={(e) => set('next_review_date', e.target.value)} />}
          </Field>
        </FormRow>
      </form>
    </Dialog>
  );
}

export default function StaffWarnings() {
  const { id } = useParams();
  const confirm = useConfirm();
  const { staff, error: staffError, reload: reloadStaff } = useStaffRecord(id);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setLoadError(null);
    api.get(`/staff/${id}/warnings`)
      .then((r) => setWarnings(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load the warnings. Try again."))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const closeDialog = useStableCallback(() => { setDialogOpen(false); setEditing(null); });

  async function deleteWarning(w) {
    if (!await confirm(`Warning #${w.warning_number || ''} (${w.issue_of_concern}) will be deleted from this staff member's record. This can't be undone.`, { title: 'Delete warning?', confirmLabel: 'Delete', danger: true })) return;
    try {
      await api.delete(`/staff/${id}/warnings/${w.id}`);
      toast.success('Warning deleted');
      setWarnings((list) => list.filter((x) => x.id !== w.id));
    } catch (e) { toast.error(e.response?.data?.message || "The warning wasn't deleted. Try again."); }
  }

  async function updateStatus(w, status) {
    try {
      await api.put(`/staff/${id}/warnings/${w.id}`, {
        issue_of_concern: w.issue_of_concern, details_of_discussion: w.details_of_discussion,
        employer_required_actions: w.employer_required_actions,
        date_communicated: dateOf(w.date_communicated) || null, next_review_date: dateOf(w.next_review_date) || null,
        status,
      });
      toast.success(`Marked ${STATUS[status].label.toLowerCase()}`);
      load();
    } catch (e) { toast.error(e.response?.data?.message || "The status wasn't changed. Try again."); }
  }

  const sorted = [...warnings].sort((a, b) => (toDay(b.date_communicated || b.created_at) || 0) - (toDay(a.date_communicated || a.created_at) || 0));
  const openNew = () => { setEditing(null); setDialogOpen(true); };

  return (
    <div>
      <StaffHeader staff={staff} error={staffError} onRetry={reloadStaff} />
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-[13px] text-slate-600">{loading ? 'Loading…' : `${plural(warnings.length, 'warning')} on file`}</p>
          <button type="button" onClick={openNew} className="btn-primary btn-sm ml-auto"><Plus size={14} aria-hidden="true" />Issue warning</button>
        </div>

        {loading ? <div className="rounded-xl border border-slate-200 bg-white"><TableSkeleton rows={3} cols={3} /></div>
          : loadError ? <div className="rounded-xl border border-slate-200 bg-white"><ErrorState message={loadError} onRetry={load} /></div>
            : sorted.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-white">
                <EmptyState icon={AlertTriangle} title="No warnings on record" hint="Record formal warnings and what was agreed, so there is a clear history if it comes up again."
                  action={<button type="button" onClick={openNew} className="btn-secondary btn-sm"><Plus size={14} aria-hidden="true" />Issue first warning</button>} />
              </div>
            ) : (
              <ul className="space-y-3">
                {sorted.map((w) => {
                  const st = STATUS[w.status] || STATUS.issued;
                  return (
                    <li key={w.id} className="rounded-xl border border-slate-200 bg-white">
                      <div className="flex flex-wrap items-start gap-3 border-b border-slate-100 px-5 py-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] font-medium text-slate-500">Warning #{w.warning_number || '—'}</p>
                          <p className="text-sm font-semibold text-slate-900">{w.issue_of_concern}</p>
                        </div>
                        <span className={st.cls}>{st.label}</span>
                        <span className="flex items-center">
                          <OverflowMenu label={`Change status of warning #${w.warning_number || ''}`} items={Object.entries(STATUS).filter(([k]) => k !== w.status).map(([k, v]) => ({ label: `Mark ${v.label.toLowerCase()}`, onClick: () => updateStatus(w, k) }))} />
                          <IconButton label="Edit warning" icon={Pencil} onClick={() => { setEditing(w); setDialogOpen(true); }} />
                          <IconButton label="Delete warning" icon={Trash2} danger onClick={() => deleteWarning(w)} />
                        </span>
                      </div>
                      <div className="grid grid-cols-1 gap-4 px-5 py-4 md:grid-cols-3">
                        <dl className="space-y-1 text-[13px]">
                          <div className="flex justify-between gap-2"><dt className="text-slate-500">Communicated</dt><dd className="text-slate-800">{showDate(w.date_communicated)}</dd></div>
                          {w.next_review_date && <div className="flex justify-between gap-2"><dt className="text-slate-500">Next review</dt><dd className="text-slate-800">{showDate(w.next_review_date)}</dd></div>}
                          {w.communicated_by_name && <div className="flex justify-between gap-2"><dt className="text-slate-500">By</dt><dd className="text-slate-800">{w.communicated_by_name}</dd></div>}
                        </dl>
                        <div className="md:col-span-2 space-y-3 text-sm">
                          <div>
                            <h4 className="text-[12px] font-semibold text-slate-500">Discussion</h4>
                            <p className="mt-0.5 whitespace-pre-wrap leading-relaxed text-slate-700">{w.details_of_discussion || <span className="text-slate-500">No details recorded.</span>}</p>
                          </div>
                          {w.employer_required_actions && (
                            <div>
                              <h4 className="text-[12px] font-semibold text-slate-500">Actions required</h4>
                              <p className="mt-0.5 whitespace-pre-wrap text-slate-700">{w.employer_required_actions}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
      </div>
      <WarningDialog open={dialogOpen} staffId={id} warning={editing} onSaved={load} onClose={closeDialog} />
    </div>
  );
}
